import {
  ExclamationCircleIcon,
  SearchIcon,
  XIcon
} from "@heroicons/react/outline";
import { useCombobox } from "downshift";
import { FieldInputProps, FormikProps } from "formik";
import { debounce, find, isEmpty } from "lodash";
import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import classNames from "../../utils/classNames";
import usePrevious from "../../utils/usePrevious";
import CenteredLoading from "../CenteredLoading";
import CheckIcon from "../icons/CheckIcon";
import { useForm } from "./FormProvider";

interface Props<Item> {
  classnames?: {
    wrapper?: string;
    input?: string;
  };
  disabled?: boolean;
  style?: React.CSSProperties;
  label?: string;
  field: FieldInputProps<any>;
  form: FormikProps<any>;
  placeholder?: string;
  helperText?: string;
  getImageUrl?: (item: Item) => string;
  itemToString: (item: Item | null) => string;
  onSelectedItem?: (item: Item | null, clearInputCallback: () => void) => void;
  fetchItems: (searchInput: string | undefined) => Promise<Item[]>;
  enableScroll?: boolean;
  withoutErrorLabel?: boolean;
  withoutMinimumSearchCharacters?: boolean;
  initialItems?: Item[];
  clearInputOnSelect?: boolean;
  debounceTime?: number;
}

function DropdownSearch<Item>({
  classnames,
  disabled,
  label,
  field,
  form: { touched, errors, submitCount, setFieldValue },
  placeholder = "Find item",
  helperText,
  getImageUrl,
  itemToString,
  fetchItems,
  onSelectedItem,
  enableScroll = true,
  withoutErrorLabel,
  withoutMinimumSearchCharacters = false,
  initialItems = [],
  clearInputOnSelect = true,
  debounceTime = 250,
  ...otherProps
}: PropsWithChildren<Props<Item>>) {
  const [fetchedItems, setFetchedItems] = useState<Item[]>(initialItems);
  const [unSavedValue, setUnSavedValue] = useState<string | undefined>(
    undefined
  );
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { isFirstErrorField } = useForm();
  const prevSubmitCount = usePrevious(submitCount);
  const inputId = `search-${field.name}-input`;

  const firstErrorField = isFirstErrorField(errors, field.name);
  const error = useMemo(
    () => (touched[field.name] || firstErrorField) && errors[field.name],
    [errors, field.name, firstErrorField, touched]
  );
  const hasError = useMemo(() => !!error, [error]);

  const scrollToElement = () => {
    if (containerRef.current?.scrollIntoView) {
      setTimeout(() => {
        containerRef.current?.scrollIntoView({ behavior: "smooth" });
      });
    }
  };

  useEffect(() => {
    if (submitCount > prevSubmitCount && firstErrorField && enableScroll) {
      scrollToElement();
    }
  }, [submitCount, prevSubmitCount, firstErrorField, enableScroll]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFetchItems = useCallback(
    debounce(async (searchInput: string | undefined) => {
      setUnSavedValue(searchInput);

      if (
        !withoutMinimumSearchCharacters &&
        (!searchInput || searchInput.trim().length <= 4)
      ) {
        setLoading(false);
        setFetchedItems([]);
        return;
      }

      const results = await fetchItems(searchInput);

      setLoading(false);

      if (!results) return;

      setFetchedItems(results);

      if (field.value?.id && !find(results, { id: field.value.id })) {
        setFieldValue(field.name, undefined);
      }
    }, debounceTime),
    [
      withoutMinimumSearchCharacters,
      field?.value?.id,
      field?.name,
      setFieldValue,
      fetchItems
    ]
  );

  const {
    isOpen,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    getInputProps,
    getComboboxProps,
    highlightedIndex,
    getItemProps,
    setInputValue,
    selectedItem,
    reset
  } = useCombobox<Item>({
    items: fetchedItems,
    onSelectedItemChange: ({ selectedItem }) =>
      setFieldValue(field.name, selectedItem),
    itemToString,
    onIsOpenChange: (input) => {
      if (input.isOpen) {
        setLoading(true);
        debouncedFetchItems(input.inputValue);
      }
    },
    onInputValueChange: ({ inputValue }) => {
      setLoading(true);
      debouncedFetchItems(inputValue);
    }
  });

  const renderItemIcon = useCallback(
    (item: Item) => {
      let imageUrl = getImageUrl && getImageUrl(item);

      if (!imageUrl) return null;

      return (
        <img
          className="flex-shrink-0 h-5 w-5 rounded-full"
          src={imageUrl}
          alt={itemToString(item)}
        />
      );
    },
    [getImageUrl, itemToString]
  );

  return (
    <div
      className={classNames(
        "mt-2 sm:gap-4 sm:items-start",
        classnames?.wrapper
      )}
      ref={containerRef}
      {...otherProps}
    >
      {label && (
        <label
          className="block text-sm font-medium text-gray-900 dark:text-white"
          {...getLabelProps()}
        >
          {label}
        </label>
      )}

      <div
        className={classNames(
          "mt-1 relative",
          (disabled === undefined ? false : disabled) &&
            "pointer-events-none opacity-50"
        )}
      >
        <span
          className="inline-block w-full rounded-md shadow-sm"
          {...getComboboxProps()}
        >
          <div className={classNames("relative", isOpen ? "block" : "hidden")}>
            <input
              type="text"
              autoFocus
              className={classNames(
                "shadow-sm block w-full text-xs sm:text-sm rounded-md pl-11 pr-3 py-2 appearance-none border-gray-300 placeholder-gray-400 focus:shadow-outline-purple focus:border-hotspotty-300 dark:bg-gray-800 dark:text-white",
                classnames?.input
              )}
              placeholder={placeholder}
              disabled={disabled === undefined ? false : disabled}
              {...getInputProps({
                id: inputId,
                onBlur: () => {
                  if (!field.value && !selectedItem) {
                    return;
                  }

                  onSelectedItem &&
                    onSelectedItem(selectedItem, () => {
                      setTimeout(() => {
                        setFieldValue(field.name, undefined);
                        setInputValue("");
                        setUnSavedValue(undefined);
                        reset();
                      });
                    });
                }
              })}
            />

            <span
              className="absolute inset-y-0 flex items-center pointer-events-none"
              style={{ left: "0.8rem" }}
            >
              {loading ? (
                <CenteredLoading className="h-5 w-5 text-gray-400" />
              ) : (
                <SearchIcon
                  className="h-5 w-5 text-gray-400"
                  aria-hidden="true"
                />
              )}
            </span>
          </div>
          <button
            type="button"
            className={classNames(
              "cursor-text relative shadow-sm w-full rounded-md pl-3 pr-10 py-2 text-left text-xs sm:text-sm sm:leading-5 bg-white disabled:bg-gray-100 border dark:bg-gray-800 dark:text-white",
              hasError ? "border-red-300 text-red-300" : "border-gray-300",
              !isOpen ? "block" : "hidden",
              classnames?.input
            )}
            {...getToggleButtonProps({
              onBlur: (e) => {
                e.stopPropagation();

                document.getElementById(inputId)?.focus();
              }
            })}
            disabled={disabled === undefined ? false : disabled}
          >
            {field.value ? (
              <div className="flex items-center space-x-3">
                {field.value && renderItemIcon(field.value)}
                <span className="block truncate dark:text-white">
                  {field.value ? itemToString(field.value) : placeholder}
                </span>
              </div>
            ) : (
              <div
                className={classNames(
                  "flex items-center space-x-3",
                  !hasError && "text-gray-400"
                )}
              >
                <SearchIcon className="flex-shrink-0 h-5 w-5" />
                <span className="block truncate">
                  {unSavedValue ? unSavedValue : placeholder}
                </span>
              </div>
            )}

            {hasError && (
              <span className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <ExclamationCircleIcon
                  className="h-5 w-5 text-red-500"
                  aria-hidden="true"
                />
              </span>
            )}
          </button>

          {(isOpen ? true : !hasError) && !!field.value && clearInputOnSelect && (
            <span className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer hover:text-gray-500 transition-color ease-in duration-100 dark:text-gray-400 dark:hover:text-white">
              <XIcon
                className="h-5 w-5"
                aria-hidden="true"
                onClick={() => {
                  setInputValue("");
                  setFieldValue(field.name, undefined);
                }}
              />
            </span>
          )}
        </span>

        <div
          className={classNames(
            "absolute mt-1 w-full rounded-md bg-white shadow-lg z-10 dark:bg-gray-800",
            !(isOpen && fetchedItems?.length > 0) && "invisible"
          )}
        >
          <ul
            className="max-h-56 rounded-md py-1 text-xs leading-6 shadow-sm overflow-auto focus:outline-none sm:text-sm sm:leading-5"
            {...getMenuProps()}
          >
            {isOpen &&
              !isEmpty(fetchedItems) &&
              fetchedItems?.map((item, index) => (
                <li
                  key={`${itemToString(item)}${index}`}
                  className={classNames(
                    "cursor-pointer select-none relative py-2 pl-3 pr-8",
                    highlightedIndex === index
                      ? "text-white bg-hotspotty-400 dark:bg-gray-700 dark:text-white"
                      : "text-gray-900 dark:bg-gray-800 dark:text-white"
                  )}
                  {...getItemProps({ item, index })}
                >
                  <div className="flex items-center space-x-3">
                    {renderItemIcon(item)}
                    <span
                      className={classNames(
                        "block truncate",
                        item === field.value ? "font-semibold" : "font-normal"
                      )}
                    >
                      {itemToString(item)}
                    </span>
                  </div>

                  {item === field.value && (
                    <span className="absolute inset-y-0 right-0 flex items-center pr-4">
                      <CheckIcon className="h-5 w-5" />
                    </span>
                  )}
                </li>
              ))}
          </ul>
        </div>
      </div>

      {!withoutErrorLabel && (helperText || hasError) && (
        <p
          className={classNames(
            "field-error mt-2 text-sm",
            hasError ? "text-red-600" : "text-gray-600 dark:text-gray-300"
          )}
        >
          {hasError ? error : helperText}
        </p>
      )}
    </div>
  );
}

export default DropdownSearch;
